import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchAccumulatedAumReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { AccumulatedAumReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { gridFormatDecimalString } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function accumulatedAumReportRowId(row: AccumulatedAumReportApiItem) {
  return String(row.user_id);
}

export function AccumulatedAumReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 110 },
    {
      field: 'total_market_value',
      headerName: 'Total market value',
      minWidth: 170,
      flex: 0.4,
      valueFormatter: gridFormatDecimalString,
    },
    { field: 'last_date', headerName: 'Last date', type: 'dateTime', width: 200 },
  ]);

  const { accumulatedAumReportItems, totalAccumulatedAumReportItems, fetchAccumulatedAumReportStatus, error } =
    useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'accumulated-aum-report.csv',
    useCallback(() => reportService.exportAccumulatedAumReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Accumulated AUM</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchAccumulatedAumReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={accumulatedAumReportItems}
          columns={columns.current}
          getRowId={accumulatedAumReportRowId}
          loading={fetchAccumulatedAumReportStatus === fetchStatuses.pending}
          rowCount={totalAccumulatedAumReportItems}
          initialSortModel={[{ field: 'last_date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
